import styled from "@emotion/styled";
import type { SelectProps as MuiSelectProps, SelectChangeEvent } from "@mui/material";
import { Chip, Divider, FormControl, FormHelperText, InputLabel, MenuItem, Select as MuiSelect } from "@mui/material";
import { camelCase } from "es-toolkit";
import { useField } from "formik";
import type { CSSProperties, ReactNode } from "react";
import { useCallback } from "react";

import { InfoTooltip } from "../../common";
import { Tooltip } from "../../dataDisplay/Tooltip";
import { Icon } from "../../icons";
import { transientOptions } from "../../utilities";
import { DEFAULT_INPUT_HEIGHT, FONT_WEIGHT_SEMI_BOLD } from "../../utilities/constants";
import { useFieldContext } from "../FormFieldContext";
import { Label } from "../Labels";
import type { TestAttribute } from "../shared";
import { DIVIDER_OPTION_VALUE, useCommitChange, useFieldLabelAndDescription, useFieldPlaceholder } from "./helpers";
import type { Option } from "./types";

const StyledFormHelperText = styled(FormHelperText)`
  margin-top: 4px;
`;

const StyledChip = styled(Chip)`
  margin: 2px;
  font-size: ${({ theme }) => theme.fontSizeSmall};
  height: 24px;

  color: ${({ theme }) => theme.secondaryStrong};
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledSelect = styled(MuiSelect<string | string[]>)`
  height: ${({ multiple }) => (multiple ? "auto" : `${DEFAULT_INPUT_HEIGHT}px`)};
  line-height: 16px;
`;

const StyledFormControl = styled(FormControl, transientOptions)<{ $direction?: "row" | "column" }>`
  width: 100%;
  ${({ $direction }) =>
    $direction === "row" &&
    `
      flex-direction: row;
      align-items: center;
      gap: 15px;

      ${Label} {
        flex: 0 0 auto;
      }

      ${StyledSelect} {
        margin-top: 0;
      }
  `};
`;

const StyledDivider = styled(Divider)`
  margin: 4px 0;
`;

const GroupMenuItem = styled(MenuItem)`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  opacity: 1;
  margin-top: 8px;
  &[class*="disabled"] {
    opacity: 1;
  }
`;

type Direction = "row" | "column";

type SelectProps<T = string | string[]> = MuiSelectProps<T> &
  TestAttribute & {
    options: readonly Option[];
    onSelectedOptionChange?: (option: Option) => void;
    helperText?: ReactNode;
    menuItemStyles?: CSSProperties;
    shrinkLabel?: boolean;
    tooltip?: ReactNode;
    direction?: Direction;
    placeholder?: string;
  };

export const Select = ({
  dataTest,
  displayEmpty = true,
  error,
  helperText,
  id,
  label,
  menuItemStyles,
  multiple,
  onChange,
  onSelectedOptionChange,
  options,
  placeholder,
  shrinkLabel,
  tooltip,
  direction,
  ...props
}: SelectProps) => {
  const handleChange = useCallback(
    (event: SelectChangeEvent<string | string[]>, child: ReactNode) => {
      const value = event.target.value;
      onChange && onChange(event, child);
      if (onSelectedOptionChange) {
        const selectedOption = options.find((option) => option.value === value);
        selectedOption && onSelectedOptionChange(selectedOption);
      }
    },
    [onChange, onSelectedOptionChange, options],
  );

  const labelId = label ? `${id ?? camelCase(label.toString())}-Label` : undefined;

  return (
    <Tooltip title={tooltip} placement="top" enterDelay={1000}>
      <StyledFormControl $direction={direction} error={error}>
        <SelectLabel
          label={label}
          direction={direction}
          labelId={labelId}
          shrink={shrinkLabel}
          helperText={helperText}
          disabled={props.disabled}
        />
        <StyledSelect
          {...props}
          labelId={labelId}
          label={label}
          size={props.size ?? "small"}
          onChange={handleChange}
          multiple={multiple ?? undefined}
          displayEmpty={displayEmpty}
          IconComponent={props.IconComponent ?? ((iconProps) => <Icon icon="angle-down" {...iconProps} />)}
          MenuProps={{
            ...props.MenuProps,
            style: { marginLeft: "4px", maxWidth: 300, ...props.MenuProps?.style },
            slotProps: {
              ...props.MenuProps?.slotProps,
              root: { "data-test": "select-menu-container" } as any,
              list: { "data-test": "list" } as any,
            },
          }}
          {...(multiple && {
            renderValue: (selected) => (
              <ChipsContainer>
                {(selected as string[]).map((value) => (
                  <StyledChip key={value} label={value} data-test="chip" />
                ))}
              </ChipsContainer>
            ),
          })}
          // note: ensure that the "active" control area is 100%
          sx={{ "div.MuiSelect-select": { maxWidth: "100%" } }}
          slotProps={{
            ...props.slotProps,
            input: {
              ...props.slotProps?.input,
              "data-test": `${dataTest ?? props.name}-input`,
            } as any,
          }}
          data-test={`${dataTest ?? props.name}-select`}
        >
          {/*
            Don't show placeholder if label is set (they overlap in the UI).
            Ternary, not `&&`: an empty-string placeholder would itself become a
            child, and MUI reads `props.value` off every child unguarded.
          */}
          {!label && placeholder ? (
            <MenuItem key="placeholder" disabled={true} value="" data-test="item">
              <span>{placeholder}</span>
            </MenuItem>
          ) : null}
          {options.map(({ label: optionLabel, value, group, disabled = false }) =>
            value === DIVIDER_OPTION_VALUE ? (
              <div key={`${group}-item`}>
                <StyledDivider key={`${value}-divider`} />
                {group && (
                  <GroupMenuItem key={`${group}-item`} value={value} disabled={true} data-test="divider">
                    {group}
                  </GroupMenuItem>
                )}
              </div>
            ) : (
              <MenuItem key={`${value}-item`} value={value} disabled={disabled} data-test="item" style={menuItemStyles}>
                {optionLabel}
              </MenuItem>
            ),
          )}
        </StyledSelect>
        {/* Note: only show helperText here if no label and can't be shown as a tooltip there. */}
        {helperText && !label && <StyledFormHelperText>{helperText}</StyledFormHelperText>}
      </StyledFormControl>
    </Tooltip>
  );
};

export type FieldSelectProps = Omit<SelectProps, "helperText"> & {
  placeholder?: string;
};

export const FieldSelect = (props: FieldSelectProps) => {
  const commitChange = useCommitChange();

  const { dataTest, name } = useFieldContext();
  const [label, helperText] = useFieldLabelAndDescription(name);
  const fieldPlaceholder = useFieldPlaceholder(name);

  const [{ value }, { error, touched }, { setValue }] = useField<string | string[]>(name);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string | string[]>, _child: ReactNode) => {
      const newValue = event.target.value as string;
      // Commit after the value settles so the submit sees the fresh value: a submit
      // validates first and abandons on error, so a pick that repairs a bad value
      // would otherwise be judged on the value it replaced, and never stored.
      setValue(newValue ?? undefined)
        .then(() => commitChange())
        .catch(console.warn);
    },
    [commitChange, setValue],
  );

  return (
    <Select
      {...props}
      id={props.id ?? name}
      variant={props.variant ?? "standard"}
      label={props.label ?? label}
      helperText={helperText}
      error={touched && Boolean(error)}
      onChange={handleChange}
      value={value ?? ""}
      dataTest={dataTest}
      placeholder={props.placeholder ?? fieldPlaceholder}
    />
  );
};

const StyledLabel = styled(InputLabel)`
  width: auto !important;
`;

type SelectLabelProps = {
  label?: ReactNode;
  direction?: Direction;
  labelId?: string;
  shrink?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
};

const SelectLabel = ({ label, direction, labelId, shrink, helperText, disabled = false }: SelectLabelProps) => {
  if (!label) return null;

  const labelContent = helperText ? (
    <>
      {label} <InfoTooltip tooltip={helperText} />
    </>
  ) : (
    label
  );

  return direction === "row" ? (
    <Label id={labelId}>{labelContent}</Label>
  ) : (
    <StyledLabel id={labelId} size="small" shrink={shrink} disabled={disabled}>
      {labelContent}
    </StyledLabel>
  );
};
